import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

// Keep in sync with the pre-paint script in public/index.html
const THEME_STORAGE_KEY = "hajex-theme-mode";

export const THEME_MODES = ["light", "dark", "system"];

const DARK_QUERY = "(prefers-color-scheme: dark)";

const ThemeModeContext = createContext(null);

const getSystemTheme = () => {
  if (typeof window === "undefined" || !window.matchMedia) return "light";
  return window.matchMedia(DARK_QUERY).matches ? "dark" : "light";
};

const readStoredMode = () => {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return THEME_MODES.includes(stored) ? stored : "light";
  } catch {
    return "light";
  }
};

// Attribute drives the CSS variables; the class keeps Tailwind's `dark:` variant
// usable, since this Tailwind version only supports the class strategy.
const applyTheme = (resolvedTheme) => {
  const root = document.documentElement;
  root.setAttribute("data-theme", resolvedTheme);
  root.classList.toggle("dark", resolvedTheme === "dark");
};

export const ThemeModeProvider = ({ children }) => {
  const [mode, setModeState] = useState(readStoredMode);
  const [systemTheme, setSystemTheme] = useState(getSystemTheme);

  const modeRef = useRef(mode);
  modeRef.current = mode;

  useEffect(() => {
    if (!window.matchMedia) return undefined;

    const query = window.matchMedia(DARK_QUERY);
    const handleChange = (event) => {
      const next = event.matches ? "dark" : "light";
      // Consumers read the CSS variables off the DOM, so the attribute has to
      // land before React re-renders with the new value.
      if (modeRef.current === "system") applyTheme(next);
      setSystemTheme(next);
    };

    // Safari < 14 only supports the deprecated listener API
    if (query.addEventListener) {
      query.addEventListener("change", handleChange);
      return () => query.removeEventListener("change", handleChange);
    }

    query.addListener(handleChange);
    return () => query.removeListener(handleChange);
  }, []);

  const resolvedTheme = mode === "system" ? systemTheme : mode;

  useEffect(() => {
    applyTheme(resolvedTheme);
  }, [resolvedTheme]);

  const setMode = useCallback((nextMode) => {
    if (!THEME_MODES.includes(nextMode)) return;

    applyTheme(nextMode === "system" ? getSystemTheme() : nextMode);
    setModeState(nextMode);

    try {
      localStorage.setItem(THEME_STORAGE_KEY, nextMode);
    } catch {
      // private mode — theme still applies for this session
    }
  }, []);

  const value = useMemo(
    () => ({ mode, resolvedTheme, setMode }),
    [mode, resolvedTheme, setMode],
  );

  return (
    <ThemeModeContext.Provider value={value}>
      {children}
    </ThemeModeContext.Provider>
  );
};

export const useThemeMode = () => {
  const context = useContext(ThemeModeContext);

  if (!context) {
    throw new Error("useThemeMode must be used within a ThemeModeProvider");
  }

  return context;
};
